import React, { Fragment } from 'react';
import styled from 'styled-components';
import { FiDownloadCloud } from 'react-icons/fi';

import { LayoutColors } from '@src/core/theme';
import { BodyExtraSmall, BodySmallMedium } from '@src/fonts/typography/Typography';

import { baseButton } from '../shared';
import useTranslate from '@src/i18n/useTranslate';
import { Voucher, SaleItem, VoucherStatus } from '@src/core/buyerApiClient';
import { device } from '@src/common/breakpoints';
import { formatDateTime } from '@src/common';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { Theme, useMediaQuery } from '@planne-software/uni/mui/material';

type Props = {
  vouchers: Voucher[];
  hasMultipleVouchers: boolean;
  saleItem: SaleItem;
  isVoucherPdfLoading: (voucher: Voucher) => boolean;
  onOpenVoucherPdf: (voucher: Voucher) => void;
};

const ReservationTable = (props: Props) => {
  const { hasMultipleVouchers, vouchers, onOpenVoucherPdf, isVoucherPdfLoading } = props;
  const { i18n } = useTranslate();

  const hasResources = vouchers.some((voucher) => voucher.resources.length);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isSplitSingle = vouchers.length === 1;
  const showDownloadButton = hasMultipleVouchers && !(isMobile && isSplitSingle);

  return (
    <Container>
      <thead>
        <tr>
          <th scope='col'>{i18n('account_sale_reservation_tariff')}</th>
          <th scope='col'>{i18n('sale_codeColumn')}</th>
          <th scope='col'>{i18n('sale_passwordColumn')}</th>
          <th scope='col'>{i18n('sale_stateColumn')}</th>
          {hasResources ? <th scope='col'>{i18n('account_sale_reservation_places')}</th> : null}
          {hasMultipleVouchers ? <th scope='col'>{i18n('account_sale_reservation_voucher')}</th> : null}
        </tr>
      </thead>
      <tbody>
        {vouchers!.map((voucher) => {
          const isPendingVoucher = voucher.status === VoucherStatus.Pending;

          return (
            <tr key={voucher.id}>
              <td data-label={i18n('account_sale_reservation_tariff')}>
                <p>
                  {voucher.tariffs.map(({ name, quantity }) => (
                    <span key={name}>
                      {name}: {quantity}
                    </span>
                  ))}
                </p>
              </td>
              <td data-label={i18n('sale_codeColumn')}>
                <p>{isPendingVoucher ? '-' : voucher.code}</p>
              </td>
              <td data-label={i18n('sale_passwordColumn')}>
                <p>{isPendingVoucher ? '-' : voucher.secret}</p>
              </td>
              <td data-label={i18n('sale_stateColumn')}>
                <p>
                  {isPendingVoucher ? (
                    <PendingVoucherStatus>{i18n('account_sale_voucher_pending_status')}</PendingVoucherStatus>
                  ) : (
                    <Fragment>
                      {voucher.revokedAt ? (
                        <Fragment>
                          <span>{i18n('account_sale_voucher_revoketed')}: </span>
                          <span id='voucher_consumed_date'>{formatDateTime(voucher.revokedAt)}</span>
                        </Fragment>
                      ) : null}
                      {!voucher.revokedAt && (voucher.consumedAt || voucher.exceptionallyConsumedAt) ? (
                        <Fragment>
                          <span>{i18n('sale_consumed')}: </span>
                          <span id='voucher_consumed_date'>
                            {formatDateTime(voucher.consumedAt ?? voucher.exceptionallyConsumedAt!)}
                          </span>
                        </Fragment>
                      ) : null}
                      {!voucher.revokedAt && !(voucher.consumedAt || voucher.exceptionallyConsumedAt) ? (
                        <span>{i18n('sale_open')}</span>
                      ) : null}
                    </Fragment>
                  )}
                </p>
              </td>

              {hasResources ? (
                <td data-label={i18n('account_sale_reservation_places')}>
                  {voucher.resources.length > 0 && isPendingVoucher ? (
                    voucher.resources.map((voucherResource) => (
                      <ResourceName key={voucherResource.resource.name}>
                        {voucherResource.resource.formattedName}
                      </ResourceName>
                    ))
                  ) : (
                    <span>-</span>
                  )}
                </td>
              ) : null}

              {showDownloadButton ? (
                <td>
                  {!voucher.revokedAt && !isPendingVoucher ? (
                    <Button onClick={() => onOpenVoucherPdf(voucher)} disabled={isVoucherPdfLoading(voucher)}>
                      {isVoucherPdfLoading(voucher) ? (
                        <DotFalling />
                      ) : (
                        <>
                          <FiDownloadCloud color={LayoutColors.whiteLight} size={22} />
                          <AccountViewText id='account_sale_vew'>{i18n('voucher_download')}</AccountViewText>
                        </>
                      )}
                    </Button>
                  ) : (
                    <span>-</span>
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </Container>
  );
};

export default ReservationTable;

const Container = styled.table`
  border: none;
  border-collapse: separate;
  border-spacing: 0 8px;
  margin: 0;
  padding: 0;
  width: 100%;
  table-layout: fixed;
  position: relative;

  thead {
    position: relative;
  }

  thead::before,
  thead::after {
    content: '';
    width: 100%;
    height: 1px;
    display: block;
    position: absolute;
    background: ${LayoutColors.grayMain};
    left: 0;
  }

  thead::after {
    bottom: 0;
  }

  thead tr th {
    ${BodyExtraSmall};
    color: ${LayoutColors.blackMain};
  }

  th,
  td {
    text-align: left;
  }

  td {
    word-break: break-all;
  }

  th:last-of-type,
  td:last-of-type {
    text-align: right;
    float: right;
  }

  tbody tr td p {
    display: flex;
    flex-direction: column;

    ${BodySmallMedium};
    color: ${LayoutColors.blackMain};
  }

  @media screen and (max-width: 750px) {
    border: 0;

    thead {
      border: none;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
      display: none;
    }

    tr {
      display: block;
    }

    td {
      ${BodySmallMedium};
      color: ${LayoutColors.blackMain};
      margin-bottom: 8px;

      text-align: right;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    td::before {
      content: attr(data-label);
      float: left;
      ${BodyExtraSmall};
      color: ${LayoutColors.blackMain};
      word-break: keep-all;
    }

    tbody tr:last-of-type {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    th:last-of-type,
    td:last-of-type {
      float: unset;
    }
  }
`;

const Button = styled.button`
  ${baseButton};
  color: ${LayoutColors.whiteLight};
  background: ${LayoutColors.justBlue};
  white-space: nowrap;

  ${device.smallTablet} {
    min-width: 54px;
    height: 42px;
  }

  & > svg {
    flex-shrink: 0;
  }

  & > span {
    word-break: keep-all;
  }
`;

const AccountViewText = styled.span`
  display: inline;
  font-size: inherit;
  color: inherit;
  font-weight: inherit;
  text-transform: uppercase;
`;

const ResourceName = styled.span`
  display: block;
`;

const PendingVoucherStatus = styled.span`
  ${BodySmallMedium};
  color: ${LayoutColors.blackMain};
  white-space: nowrap;
`;
